/**
 * Simple Image/Video Lazy Loader
 * Prevents images and videos from loading until section is visible
 * HTML structure stays intact - only resource loading is delayed
 */

(function() {
  'use strict';

  class SimpleLazyLoader {
    constructor(options = {}) {
      this.options = {
        rootMargin: options.rootMargin || '200px',
        threshold: options.threshold || 0.01,
        sectionSelector: options.sectionSelector || '.dynamic-sections .shopify-section',
        startFromSection: options.startFromSection || 3,
        ...options
      };

      this.observer = null;
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      // Skip in theme editor
      if (window.Shopify && window.Shopify.designMode) {
        console.log('Theme editor detected, skipping lazy loading');
        return;
      }

      // Skip if Intersection Observer not supported
      if (!('IntersectionObserver' in window)) {
        console.warn('IntersectionObserver not supported');
        return;
      }

      this.createObserver();
      this.observeSections();
    }

    createObserver() {
      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersection(entries),
        {
          rootMargin: this.options.rootMargin,
          threshold: this.options.threshold
        }
      );
    }

    observeSections() {
      const sections = document.querySelectorAll(this.options.sectionSelector);
      let lazyCount = 0;

      sections.forEach((section, index) => {
        // Skip first N sections (hero, intro, etc)
        if (index < this.options.startFromSection - 1) {
          return;
        }

        // If section is already in viewport, load it immediately
        if (this.isInViewport(section)) {
          this.loadSectionResources(section);
          return;
        }

        // Mark section for lazy loading
        section.classList.add('lazy-section');
        section.setAttribute('data-lazy-state', 'pending');

        // Observe section
        this.observer.observe(section);
        lazyCount++;
      });

      console.log(`✅ Lazy loading active for ${lazyCount} sections`);
    }

    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          
          // Load resources for this section
          this.loadSectionResources(section);
          
          // Stop observing
          this.observer.unobserve(section);
        }
      });
    }

    loadSectionResources(section) {
      section.setAttribute('data-lazy-state', 'loading');

      // Restore images
      const images = section.querySelectorAll('img[data-lazy-src]');
      images.forEach(img => {
        if (img.dataset.lazySrc) {
          img.src = img.dataset.lazySrc;
          img.removeAttribute('data-lazy-src');
        }
        if (img.dataset.lazySrcset) {
          img.srcset = img.dataset.lazySrcset;
          img.removeAttribute('data-lazy-srcset');
        }
      });

      // Restore videos - handle both video tags and source tags
      const videos = section.querySelectorAll('video');
      videos.forEach(video => {
        let needsReload = false;
        
        // Check if video has direct src
        if (video.dataset.lazySrc) {
          video.src = video.dataset.lazySrc;
          video.removeAttribute('data-lazy-src');
          needsReload = true;
        }
        
        // Check source tags inside video
        const sources = video.querySelectorAll('source[data-lazy-src]');
        sources.forEach(source => {
          if (source.dataset.lazySrc) {
            source.src = source.dataset.lazySrc;
            source.removeAttribute('data-lazy-src');
            needsReload = true;
          }
        });
        
        // Reload video if sources were updated
        if (needsReload) {
          video.load();
        }
      });

      // Restore background images
      const bgElements = section.querySelectorAll('[data-lazy-bg]');
      bgElements.forEach(el => {
        if (el.dataset.lazyBg) {
          el.setAttribute('style', el.dataset.lazyBg);
          el.removeAttribute('data-lazy-bg');
        }
      });

      // Mark as loaded
      setTimeout(() => {
        section.setAttribute('data-lazy-state', 'loaded');
        section.classList.add('lazy-section--loaded');
      }, 100);

      console.log(`✅ Loaded resources for section ${section.id || 'unnamed'}`);
    }

    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < windowHeight + 100;
    }
  }

  // Auto-initialize
  let loader;
  
  function initLazyLoader(customOptions = {}) {
    if (loader) {
      console.log('Lazy loader already initialized');
      return;
    }
    
    loader = new SimpleLazyLoader(customOptions);
    window.LazyLoader = loader;
  }

  // Initialize with defaults
  initLazyLoader({
    sectionSelector: '.dynamic-sections .shopify-section',
    startFromSection: 3,
    rootMargin: '200px'
  });

  // Expose for customization
  window.initLazyLoader = initLazyLoader;

})();
